import React, {lazy, Suspense} from 'react'
import {Provider} from 'react-redux'
import {ConnectedRouter} from 'react-router-redux'
import {hot} from 'react-hot-loader'
import {LastLocationProvider} from 'react-router-last-location'
import {createBrowserHistory} from 'history'
import {StyleRoot} from 'radium'
import MuiThemeProviderV1 from '@material-ui/core/styles/MuiThemeProvider'
import createStore from 'store/store'
import DynamicMuiThemeProvider from './theme/DynamicMuiThemeProvider'
import {ThemeProvider} from 'theme/ThemeProvider'
import muiV1Theme from './theme/muiV1Theme'
import iconNames from 'theme/icons'
import ErrorReload from './containers/errorWrappers/errorReload'
import Button from 'components/button'
import componentLoader from 'components/componentLoader'
import Loading from 'components/loading'
import Text from 'components/text'
import Icon from 'components/icon'
import {handleChunkError, isChunkError} from "helpers/error"

const RootScene = lazy(() =>
  componentLoader(import('scenes/rootScene')))

const errorPageStyling = {
  container: {
    width: '100%',
    paddingTop: 120,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 24,
    color: '#aaa',
  },
  icon: {
    marginRight: 12,
  },
  message: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  title: {
    fontSize: 16,
    fontWeight: 400,
    paddingBottom: 12,
  },
  details: {
    marginTop: 80,
    paddingTop: 40,
    minWidth: 380,
    borderTop: '1px solid #ddd',
    fontSize: 11,
    textAlign: 'center',
  },
}

export const history = createBrowserHistory()
export const store = createStore(history)

class App extends React.Component {
  constructor(props) {
    super(props)
    this.state = {
      hasError: false,
      error: null,
      info: null,
    }
  }

  componentDidMount() {
    window.addEventListener('touchstart', this.detectTouch)
    window.addEventListener('keydown', this.detectKeyDown)

    // set the body color so the mobile tab looks nice
    document.body.classList.toggle('body-loaded', true)
  }

  componentWillUnmount() {
    window.removeEventListener('touchstart', this.detectTouch)
    window.removeEventListener('keydown', this.detectKeyDown)
  }

  componentDidCatch(error, info) {
    if (isChunkError(error) && handleChunkError())
      return

    this.setState({
      hasError: true,
      error,
      info,
    })
  }

  detectTouch = () => {
    window.isTouch = true
    window.removeEventListener('touchstart', this.detectTouch)
    this.forceUpdate()
  }

  getContent = () => {
    const {hasError, error, info} = this.state
    if (hasError) {
      return error === 'ChunkLoadError'
        ? <ErrorReload/>
        : (
          <div style={errorPageStyling.container}>
            <div style={errorPageStyling.message}>
              <Icon
                name={iconNames.bug}
                size={35}
                color={'var(--cg-color-text-disabled)'}
                style={errorPageStyling.icon}/>
              Sorry! Something went wrong
            </div>
            <Button onClick={() => {
              window.location.reload()
            }} flat label={'Reload'}/>
            <div style={errorPageStyling.details}>
              <div style={errorPageStyling.title}>Technical Details</div>
              <b>{error.message} ({error.fileName}:{error.lineNumber})</b>
              <Text>{info.componentStack}</Text>
            </div>
          </div>
        )
    }

    return (
      <Suspense fallback={<Loading transparent={true}/>}>
        <RootScene/>
      </Suspense>
    )
  }

  render() {
    return (
      <StyleRoot>
        <Provider store={store}>
          <ThemeProvider>
            <DynamicMuiThemeProvider>
              <MuiThemeProviderV1 theme={muiV1Theme}>
                <ConnectedRouter history={history}>
                  <LastLocationProvider watchOnlyPathname={true}>
                    {this.getContent()}
                  </LastLocationProvider>
                </ConnectedRouter>
              </MuiThemeProviderV1>
            </DynamicMuiThemeProvider>
          </ThemeProvider>
        </Provider>
      </StyleRoot>
    )
  }
}

// Prevent file drag-drop into the browser (unless explicitly implemented)
window.addEventListener('dragover', e => e.preventDefault(), false)
window.addEventListener('drop', e => e.preventDefault(), false)

// Pass the store to Cypress to dispatch actions
if (window.Cypress) {
  window.store = store
}

export default hot(module)(App)
